/**
 * Merge confirmed iCal + Manual/AirBnB Export pairs into one Bookings-tab row.
 * Prefer Manual fields for guests/payout; keep reservation_url from either side.
 */

import { airbnbConfirmationCodeFromBooking } from './airbnbConfirmationCode';
import { isCancelledBookingStatus } from './bookingStatus';

export type MergeableBooking = {
  id: number;
  source: string;
  guest_name?: string | null;
  guest_email?: string | null;
  guest_phone_last_4?: string | null;
  reservation_url?: string | null;
  adults_count?: number | null;
  children_count?: number | null;
  total_payout_amount?: number | null;
  host_payout_amount?: number | null;
  currency?: string;
  external_booking_id?: string | null;
  booking_ref?: string;
  [key: string]: unknown;
};

export type ConfirmedBookingLink = {
  id: number;
  ical_booking_id: number;
  manual_booking_id: number;
  confirmed_by_user?: boolean;
  confidence_score?: number | null;
  ical_booking?: MergeableBooking | null;
  manual_booking?: MergeableBooking | null;
};

/** System/user certainty that the pair is the same physical booking. */
export function isHighConfidenceConfirmedLink(
  link: ConfirmedBookingLink,
  ical: MergeableBooking,
  manual: MergeableBooking
): boolean {
  if (link.confirmed_by_user) return true;
  const icalCode = airbnbConfirmationCodeFromBooking(ical);
  const manualCode = airbnbConfirmationCodeFromBooking(manual);
  if (icalCode && manualCode && icalCode === manualCode) return true;
  const score = Number(link.confidence_score);
  return Number.isFinite(score) && score >= 1;
}

function pickReservationUrl(a: MergeableBooking, b: MergeableBooking): string | undefined {
  const urls = [a.reservation_url, b.reservation_url]
    .map((u) => (typeof u === 'string' ? u.trim() : ''))
    .filter(Boolean);
  return urls[0] || undefined;
}

function preferManualValue<T>(
  manual: MergeableBooking,
  ical: MergeableBooking,
  key: keyof MergeableBooking
): T | undefined {
  const m = manual[key];
  if (m != null && !(typeof m === 'string' && !String(m).trim())) {
    return m as T;
  }
  const i = ical[key];
  if (i != null && !(typeof i === 'string' && !String(i).trim())) {
    return i as T;
  }
  return undefined;
}

function preferStatus(
  manual: MergeableBooking,
  ical: MergeableBooking
): string | undefined {
  const manualStatus = manual.status != null ? String(manual.status) : '';
  const icalStatus = ical.status != null ? String(ical.status) : '';
  // Cancellation on either side wins (export often has "cancelled by guest" while iCal stays confirmed).
  if (isCancelledBookingStatus(manualStatus)) return manualStatus;
  if (isCancelledBookingStatus(icalStatus)) return icalStatus;
  return (
    (preferManualValue(manual, ical, 'status') as string | undefined) ||
    manualStatus ||
    icalStatus ||
    undefined
  );
}

export function buildMergedCombinedBooking(
  ical: MergeableBooking,
  manual: MergeableBooking,
  link: ConfirmedBookingLink
): MergeableBooking {
  const reservation_url = pickReservationUrl(manual, ical) || pickReservationUrl(ical, manual);
  const booking_ref =
    airbnbConfirmationCodeFromBooking(manual) ||
    airbnbConfirmationCodeFromBooking(ical) ||
    manual.booking_ref ||
    ical.booking_ref;

  return {
    ...ical,
    ...manual,
    // Canonical detail target: Manual/Export (richer / usually correct guests).
    id: manual.id,
    source: 'Merged',
    status: preferStatus(manual, ical),
    guest_name: preferManualValue(manual, ical, 'guest_name'),
    guest_email: preferManualValue(manual, ical, 'guest_email'),
    guest_phone_last_4: preferManualValue(manual, ical, 'guest_phone_last_4'),
    adults_count: preferManualValue(manual, ical, 'adults_count') as number | undefined,
    children_count: preferManualValue(manual, ical, 'children_count') as number | undefined,
    total_payout_amount: preferManualValue(manual, ical, 'total_payout_amount') as number | undefined,
    host_payout_amount: preferManualValue(manual, ical, 'host_payout_amount') as number | undefined,
    currency: (preferManualValue(manual, ical, 'currency') as string | undefined) || manual.currency || ical.currency,
    external_booking_id: preferManualValue(manual, ical, 'external_booking_id'),
    reservation_url,
    booking_ref,
    merged_booking_ids: [ical.id, manual.id],
    linked_ical_booking_id: ical.id,
    linked_manual_booking_id: manual.id,
    booking_link_id: link.id,
    is_merged_pair: true,
    merged_from_sources: [ical.source, manual.source].filter(Boolean),
  };
}

/**
 * Collapse high-confidence confirmed pairs into one row for the Bookings list tab.
 * Unmatched bookings pass through unchanged. Order is preserved by first-seen index.
 */
export function mergeHighConfidenceCombinedBookings<T extends MergeableBooking>(
  bookings: T[],
  links: ConfirmedBookingLink[]
): T[] {
  if (!bookings.length || !links.length) return bookings;

  const byId = new Map<number, T>();
  bookings.forEach((b) => byId.set(b.id, b));

  const suppressed = new Set<number>();
  const merges: Array<{ index: number; row: T }> = [];

  for (const link of links) {
    const ical = byId.get(link.ical_booking_id);
    const manual = byId.get(link.manual_booking_id);
    if (!ical || !manual) continue;
    if (suppressed.has(ical.id) || suppressed.has(manual.id)) continue;
    if (!isHighConfidenceConfirmedLink(link, ical, manual)) continue;

    const icalIndex = bookings.findIndex((b) => b.id === ical.id);
    const manualIndex = bookings.findIndex((b) => b.id === manual.id);
    const index = Math.min(
      icalIndex >= 0 ? icalIndex : Number.MAX_SAFE_INTEGER,
      manualIndex >= 0 ? manualIndex : Number.MAX_SAFE_INTEGER
    );

    suppressed.add(ical.id);
    suppressed.add(manual.id);
    merges.push({
      index,
      row: buildMergedCombinedBooking(ical, manual, link) as T,
    });
  }

  if (!merges.length) return bookings;

  merges.sort((a, b) => a.index - b.index);
  const result: T[] = [];
  let mergeIdx = 0;
  bookings.forEach((b, i) => {
    while (mergeIdx < merges.length && merges[mergeIdx].index === i) {
      result.push(merges[mergeIdx].row);
      mergeIdx += 1;
    }
    if (!suppressed.has(b.id)) {
      result.push(b);
    }
  });
  while (mergeIdx < merges.length) {
    result.push(merges[mergeIdx].row);
    mergeIdx += 1;
  }
  return result;
}
